import { atom, getDefaultStore } from 'jotai';
import type { TeamSubscriptionStatus } from 'quadratic-shared/typesAndSchemas';

export type BillingPlanType = 'FREE' | 'PRO' | 'BUSINESS';

export interface TeamBillingState {
  isOnPaidPlan: boolean;
  planType: BillingPlanType;
  billingStatus: TeamSubscriptionStatus | undefined;
  allowOveragePayments: boolean;
  teamMonthlyBudgetLimit: number | null;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
  scheduledPlanType: BillingPlanType | null;
}

const defaultState: TeamBillingState = {
  isOnPaidPlan: false,
  planType: 'FREE',
  billingStatus: undefined,
  allowOveragePayments: false,
  teamMonthlyBudgetLimit: null,
  cancelAtPeriodEnd: false,
  currentPeriodEnd: null,
  scheduledPlanType: null,
};

const baseTeamBillingAtom = atom<TeamBillingState>(defaultState);

export const teamBillingAtom = atom(
  (get) => get(baseTeamBillingAtom),
  (get, set, updates: Partial<TeamBillingState>) => {
    set(baseTeamBillingAtom, { ...get(baseTeamBillingAtom), ...updates });
  }
);

/**
 * Update team billing state from any context (React or non-React).
 * Merges the provided updates with the current state.
 */
export const updateTeamBilling = (updates: Partial<TeamBillingState>) => {
  getDefaultStore().set(teamBillingAtom, updates);
};

/**
 * Set the allowOveragePayments value.
 * Convenience helper for updating just this field.
 */
export const setAllowOveragePayments = (value: boolean) => {
  updateTeamBilling({ allowOveragePayments: value });
};

/**
 * Set the teamMonthlyBudgetLimit value.
 * Convenience helper for updating just this field.
 */
export const setTeamMonthlyBudgetLimit = (value: number | null) => {
  updateTeamBilling({ teamMonthlyBudgetLimit: value });
};

/**
 * Set the full billing state.
 * Used when initializing from loader data.
 */
export const setTeamBilling = (state: TeamBillingState) => {
  getDefaultStore().set(baseTeamBillingAtom, state);
};

/**
 * Whether the team's payment has failed (PAST_DUE or UNPAID).
 * Reads from the atom store so it can be used outside React components.
 */
export const getIsPaymentFailed = (): boolean => {
  const { billingStatus } = getDefaultStore().get(teamBillingAtom);
  return billingStatus === 'PAST_DUE' || billingStatus === 'UNPAID';
};

/**
 * Whether the team is on a paid plan (active Pro or Business subscription).
 * Reads from the atom store so it can be used outside React components.
 */
export const getIsOnPaidPlan = (): boolean => {
  return getDefaultStore().get(teamBillingAtom).isOnPaidPlan;
};

/**
 * Get the next plan to suggest when the user exceeds their AI limit.
 * - Free → Pro
 * - Pro → Business
 * - Business → Business+overage (if overage is not enabled)
 */
export const getNextPlanSuggestion = (
  planType: BillingPlanType,
  allowOveragePayments: boolean
): { type: 'upgrade'; targetPlan: 'PRO' | 'BUSINESS' } | { type: 'enableOverage' } | null => {
  switch (planType) {
    case 'FREE':
      return { type: 'upgrade', targetPlan: 'PRO' };
    case 'PRO':
      return { type: 'upgrade', targetPlan: 'BUSINESS' };
    case 'BUSINESS':
      return allowOveragePayments ? null : { type: 'enableOverage' };
    default:
      return { type: 'upgrade', targetPlan: 'PRO' };
  }
};
